/**
 * useAudio hook
 *
 * Manages audio playback using Tone.js and the Web Audio API.
 * Provides loading progress tracking for loading bar display.
 */

import { useRef, useState, useCallback, useEffect } from 'react';
import * as Tone from 'tone';

export interface UseAudioReturn {
  /** Load an audio file URL into the player */
  loadAudio: (url: string) => Promise<void>;
  /** Start playback from the given offset in seconds */
  startPlayback: (offsetSeconds?: number) => Promise<void>;
  /** Stop and reset to beginning */
  stopPlayback: () => void;
  /** Pause playback */
  pausePlayback: () => void;
  /** Resume after pause */
  resumePlayback: () => Promise<void>;
  /** Set audio playback rate (0.5 = half speed, 1 = normal, 1.5 = 1.5× speed) */
  setPlaybackRate: (rate: number) => void;
  /** Current playback position in ms (updates ~60fps) */
  currentTimeMs: number;
  /** Is audio currently playing */
  isPlaying: boolean;
  /** Is the player loaded and ready */
  isLoaded: boolean;
  /** Loading progress 0-100 */
  loadingProgress: number;
  /** Is audio currently loading */
  isLoading: boolean;
  /** Loading/error state */
  error: string | null;
  /** Play a single metronome click (for count-in) */
  playMetronomeClick: (isAccented?: boolean) => void;
  /** Tone.js Transport time for precise sync */
  getTransportTime: () => number;
  /** Whether AudioContext needs user interaction to start */
  needsUserInteraction: boolean;
  /** Initialize AudioContext (call after user gesture) */
  initAudioContext: () => Promise<void>;
}

export function useAudio(): UseAudioReturn {
  const playerRef = useRef<Tone.Player | null>(null);
  const metronomeRef = useRef<Tone.MetalSynth | null>(null);
  const startOffsetRef = useRef<number>(0);
  const pausedAtRef = useRef<number>(0);
  const playbackRateRef = useRef<number>(1);
  const startAudioOffsetMsRef = useRef<number>(0);

  const [currentTimeMs, setCurrentTimeMs] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsUserInteraction, setNeedsUserInteraction] = useState(false);

  const rafRef = useRef<number>(0);
  const abortControllerRef = useRef<AbortController | null>(null);
  const pendingUrlRef = useRef<string | null>(null);
  const isLoadingRef = useRef<boolean>(false);

  // Time-update loop - accounts for playback rate to stay in sync with audio
  const startTimeLoop = useCallback(() => {
    const tick = () => {
      if (playerRef.current && isPlaying) {
        const now = Tone.now();
        // elapsedAudioTime = wallClockTime * playbackRate + audioOffset
        const elapsed = (now - startOffsetRef.current) * 1000 * playbackRateRef.current 
          + startAudioOffsetMsRef.current;
        setCurrentTimeMs(Math.max(0, elapsed));
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [isPlaying]);

  const stopTimeLoop = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
  }, []);

  // Forward declaration - actual implementation defined below
  const loadAudioRef = useRef<(url: string) => Promise<void>>();

  /** Initialize AudioContext after user gesture */
  const initAudioContext = useCallback(async (): Promise<void> => {
    try {
      await Tone.start();
      setNeedsUserInteraction(false);
      
      // If there's a pending URL, load it now
      if (pendingUrlRef.current && loadAudioRef.current) {
        const url = pendingUrlRef.current;
        pendingUrlRef.current = null;
        await loadAudioRef.current(url);
      }
    } catch (e) {
      setError(`Audio init error: ${(e as Error).message}`);
    }
  }, []);

  /** Load an audio URL with progress tracking */
  const loadAudio = useCallback(async (url: string): Promise<void> => {
    // Check if AudioContext needs user interaction
    if (Tone.context.state !== 'running') {
      // Don't overwrite if already loading - queue the URL
      if (!isLoadingRef.current) {
        pendingUrlRef.current = url;
        setNeedsUserInteraction(true);
      }
      return;
    }
    
    // Prevent concurrent loads
    if (isLoadingRef.current) {
      return;
    }
    isLoadingRef.current = true;

    // Cancel any ongoing load
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Reset state
    setIsLoaded(false);
    setIsLoading(true);
    setLoadingProgress(0);
    setError(null);

    // Dispose previous player
    if (playerRef.current) {
      playerRef.current.dispose();
      playerRef.current = null;
    }

    abortControllerRef.current = new AbortController();

    try {
      // Fetch audio with progress tracking
      const response = await fetch(url, {
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to load audio: ${response.status} ${response.statusText}`);
      }

      const contentLength = response.headers.get('content-length');
      const total = contentLength ? parseInt(contentLength, 10) : 0;

      let audioBlob: Blob;

      if (!total || !response.body) {
        // No progress tracking available, just download
        audioBlob = await response.blob();
        setLoadingProgress(50);
      } else {
        // Track progress with ReadableStream
        const reader = response.body.getReader();
        let receivedLength = 0;
        const chunks: Uint8Array[] = [];

        while (true) {
          const { done, value } = await reader.read();

          if (done) break;

          chunks.push(value);
          receivedLength += value.length;

          const newProgress = (receivedLength / total) * 100;
          setLoadingProgress(Math.min(newProgress, 99));
        }

        // Combine chunks
        const allChunks = new Uint8Array(receivedLength);
        let position = 0;
        for (const chunk of chunks) {
          allChunks.set(chunk, position);
          position += chunk.length;
        }

        audioBlob = new Blob([allChunks], { type: 'audio/wav' });
      }

      // Create blob URL and load into Tone.Player
      const blobUrl = URL.createObjectURL(audioBlob);
      setLoadingProgress(100);

      const player = new Tone.Player({
        url: blobUrl,
        onload: () => {
          setIsLoaded(true);
          setIsLoading(false);
        },
        onerror: (e) => {
          setError(`Failed to decode audio: ${e}`);
          setIsLoading(false);
          URL.revokeObjectURL(blobUrl);
        },
      }).toDestination();

      playerRef.current = player;
    } catch (e) {
      if (e instanceof Error && e.name === 'AbortError') {
        return;
      }
      setError(`Audio load error: ${(e as Error).message}`);
      setIsLoading(false);
    } finally {
      isLoadingRef.current = false;
    }
  }, []);

  // Store reference for initAudioContext to use
  loadAudioRef.current = loadAudio;

  /** Start playback from the given offset in seconds */
  const startPlayback = useCallback(
    async (offsetSeconds: number = 0): Promise<void> => {
      if (!playerRef.current) return;
      
      // Resume AudioContext after user gesture (required by browsers)
      if (Tone.context.state !== 'running') {
        await Tone.start();
      }

      try {
        playerRef.current.start(Tone.now(), offsetSeconds);
        startOffsetRef.current = Tone.now();
        startAudioOffsetMsRef.current = offsetSeconds * 1000;
        setIsPlaying(true);
      } catch (e) {
        setError(`Playback error: ${(e as Error).message}`);
      }
    },
    []
  );

  /** Stop and reset */
  const stopPlayback = useCallback(() => {
    if (playerRef.current) {
      try {
        playerRef.current.stop();
      } catch {
        /* already stopped */
      }
    }
    setIsPlaying(false);
    setCurrentTimeMs(0);
    startOffsetRef.current = 0;
    pausedAtRef.current = 0;
    startAudioOffsetMsRef.current = 0;
    stopTimeLoop();
  }, [stopTimeLoop]);

  /** Pause */
  const pausePlayback = useCallback(() => {
    if (playerRef.current) {
      try {
        playerRef.current.stop();
      } catch {
        /* already stopped */
      }
    }
    pausedAtRef.current = currentTimeMs;
    setIsPlaying(false);
    stopTimeLoop();
  }, [currentTimeMs, stopTimeLoop]);

  /** Resume */
  const resumePlayback = useCallback(async (): Promise<void> => {
    await startPlayback(pausedAtRef.current / 1000);
  }, [startPlayback]);

  /** Set playback rate on the Tone.Player (0.5–2.0) */
  const setPlaybackRate = useCallback((rate: number) => {
    playbackRateRef.current = rate;
    if (playerRef.current) {
      playerRef.current.playbackRate = rate;
    }
  }, []);

  /** Get raw Tone.Transport time */
  const getTransportTime = useCallback((): number => {
    return Tone.now() * 1000;
  }, []);

  /** Play a metronome click */
  const playMetronomeClick = useCallback((isAccented: boolean = false) => {
    if (!metronomeRef.current) {
      metronomeRef.current = new Tone.MetalSynth({
        envelope: { attack: 0.001, decay: 0.1, release: 0.1 },
        harmonicity: 5.1,
        modulationIndex: 32,
        resonance: isAccented ? 4000 : 2000,
        octaves: 1.5,
      }).toDestination();
    }

    metronomeRef.current.triggerAttackRelease(
      isAccented ? '8n' : '16n',
      Tone.now()
    );
  }, []);

  // Start/stop the time loop based on isPlaying
  useEffect(() => {
    if (isPlaying) {
      startTimeLoop();
    } else {
      stopTimeLoop();
    }
    return () => stopTimeLoop();
  }, [isPlaying, startTimeLoop, stopTimeLoop]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopTimeLoop();
      playerRef.current?.dispose();
      metronomeRef.current?.dispose();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [stopTimeLoop]);

  return {
    loadAudio,
    startPlayback,
    stopPlayback,
    pausePlayback,
    resumePlayback,
    setPlaybackRate,
    currentTimeMs,
    isPlaying,
    isLoaded,
    loadingProgress,
    isLoading,
    error,
    playMetronomeClick,
    getTransportTime,
    needsUserInteraction,
    initAudioContext,
  };
}
